import { useEffect, useState } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";

import { withLang, type Lang } from "@/lib/lang-url";

/** The browser languages we can actually serve. */

const LABEL: Record<Lang, string> = {
  en: "",
  pt: "Ver este site em português",
  es: "Ver este sitio en español",
};

const CHOSEN_KEY = "ljp-lang-offered";

/**
 * English is the default address, so a visitor whose browser is Portuguese or
 * Spanish is offered their own address instead of being moved there by surprise.
 * Google keeps seeing the English page either way.
 */
export function LangOffer() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [offer, setOffer] = useState<Lang | null>(null);

  useEffect(() => {
    // An address that already names a language needs no offer.
    if (router.$lang.get() !== "en") return;
    const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
    const found = prefs
      .map((p) => (p || "").slice(0, 2).toLowerCase())
      .find((code) => code === "pt" || code === "es");
    if (!found) return;
    const lang = found as Lang;
    if (window.localStorage.getItem(CHOSEN_KEY) === lang) return;
    setOffer(lang);
  }, [router]);

  if (!offer) return null;

  const dismiss = () => {
    window.localStorage.setItem(CHOSEN_KEY, offer);
    setOffer(null);
  };

  return (
    <div className="fixed bottom-4 left-1/2 z-[90] flex w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 items-center justify-between gap-4 border border-bone/25 bg-ink/95 px-4 py-3 backdrop-blur-sm md:bottom-6 md:left-auto md:right-6 md:translate-x-0">
      <a
        href={withLang(pathname, offer)}
        onClick={dismiss}
        className="font-mono text-[9px] uppercase leading-[1.6] tracking-[0.22em] text-bone/75 transition-colors hover:text-bone"
      >
        {LABEL[offer]}
        <span className="ml-2 text-bone/65">↗</span>
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Close"
        className="shrink-0 font-mono text-[11px] leading-none text-bone/65 transition-colors hover:text-bone"
      >
        ×
      </button>
    </div>
  );
}
